import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { api, errMsg, fmtDate, useSEO } from "../../lib/api";
import { useShop } from "../../context/ShopContext";

const inp = "w-full border border-[#E8E0D5] rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-[#6B8E78]";

export function CmsPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    setP(null);
    api.get(`/pages/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
    if (slug === "faq") api.get("/faqs").then((r) => setFaqs(r.data));
  }, [slug]);
  useSEO({ title: p?.seo_title || p?.title, description: p?.seo_description });
  if (p === false) return <div className="container-x py-24"><h1 className="text-4xl text-[#1B3B2B]">This page is not published yet</h1></div>;
  if (!p) return <div className="container-x py-24">Loading…</div>;
  return (
    <div className="container-x py-14 max-w-3xl" data-testid={`cms-page-${slug}`}>
      <p className="eyebrow mb-2">SEED COSMETICS</p>
      <h1 className="text-4xl sm:text-5xl text-[#1B3B2B] tracking-tight mb-8">{p.title}</h1>
      <div className="prose-seed">{p.content.split(/\n\n+/).map((para, i) => <p key={i} className="whitespace-pre-line">{para}</p>)}</div>
      {faqs.length > 0 && <Accordion type="single" collapsible className="mt-6">{faqs.map((f) => <AccordionItem key={f.id} value={f.id}><AccordionTrigger className="text-left">{f.question}</AccordionTrigger><AccordionContent className="text-stone-600">{f.answer}</AccordionContent></AccordionItem>)}</Accordion>}
    </div>
  );
}

export function Contact() {
  const { settings } = useShop();
  const c = settings.contact || {};
  const [f, setF] = useState({ name: "", email: "", mobile: "", subject: "", message: "" });
  const [pv, setPv] = useState({ kind: "access", mobile: "", email: "", details: "" });
  useSEO({ title: "Contact Us" });
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/contact", { ...f, mobile: f.mobile || null }); toast.success("Message sent. We'll get back to you soon."); setF({ name: "", email: "", mobile: "", subject: "", message: "" }); } catch (er) { toast.error(errMsg(er)); }
  };
  const sendPv = async (e) => {
    e.preventDefault();
    try { await api.post("/privacy/request", { ...pv, mobile: pv.mobile || null, email: pv.email || null }); toast.success("Request received"); setPv({ kind: "access", mobile: "", email: "", details: "" }); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="container-x py-14 grid lg:grid-cols-2 gap-12">
      <div className="space-y-4">
        <p className="eyebrow">We're here to help</p>
        <h1 className="text-4xl sm:text-5xl text-[#1B3B2B]">Contact us</h1>
        <div className="text-stone-600 space-y-1 text-sm" data-testid="contact-details">
          {c.email && <p>Email: <a className="underline" href={`mailto:${c.email}`}>{c.email}</a></p>}
          {c.phone && <p>Phone: <a className="underline" href={`tel:${c.phone}`}>{c.phone}</a></p>}
          {c.whatsapp && <p>WhatsApp: <a className="underline" href={`https://wa.me/91${c.whatsapp}`}>{c.whatsapp}</a></p>}
          {c.address && <p>{c.address}</p>}{c.hours && <p>{c.hours}</p>}
        </div>
        <form onSubmit={sendPv} className="border border-[#E8E0D5] rounded-2xl p-5 space-y-3 bg-[#FFFDF9] mt-8" data-testid="privacy-request-form">
          <p className="font-medium text-[#1B3B2B]">Your data rights</p>
          <p className="text-xs text-stone-500">Request access to, correction of, or deletion of the personal data we hold about you.</p>
          <select className={inp} value={pv.kind} onChange={(e) => setPv({ ...pv, kind: e.target.value })} data-testid="privacy-kind-select"><option value="access">Access my data</option><option value="correction">Correct my data</option><option value="deletion">Delete my data</option></select>
          <div className="grid sm:grid-cols-2 gap-3"><input className={inp} placeholder="Mobile" value={pv.mobile} onChange={(e) => setPv({ ...pv, mobile: e.target.value })} data-testid="privacy-mobile-input" /><input className={inp} placeholder="Email" type="email" value={pv.email} onChange={(e) => setPv({ ...pv, email: e.target.value })} data-testid="privacy-email-input" /></div>
          <textarea className={inp} rows={2} placeholder="Details (optional)" value={pv.details} onChange={(e) => setPv({ ...pv, details: e.target.value })} />
          <button className="border border-[#1B3B2B] rounded-full px-5 py-2 text-sm" data-testid="privacy-submit-button">Submit request</button>
        </form>
      </div>
      <form onSubmit={send} className="border border-[#E8E0D5] rounded-2xl p-6 space-y-3 bg-[#FFFDF9] h-fit" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input required className={inp} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" />
          <input required type="email" className={inp} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" />
        </div>
        <input className={inp} placeholder="Mobile (optional)" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="contact-mobile-input" />
        <input className={inp} placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject-input" />
        <textarea required minLength={5} rows={5} className={inp} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" />
        <button className="bg-[#1B3B2B] text-white rounded-full px-6 py-3 text-sm" data-testid="contact-submit-button">Send message</button>
      </form>
    </div>
  );
}

export function TrackOrder() {
  const [f, setF] = useState({ order_number: "", mobile: "" });
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useSEO({ title: "Track Order" });
  const go = async (e) => {
    e.preventDefault();
    setErr("");
    try { const { data } = await api.get("/orders/track", { params: f }); setO(data); } catch (er) { setO(null); setErr(errMsg(er)); }
  };
  return (
    <div className="container-x py-14 max-w-xl">
      <h1 className="text-4xl sm:text-5xl text-[#1B3B2B] mb-6">Track your order</h1>
      <form onSubmit={go} className="space-y-3" data-testid="track-order-form">
        <input required className={inp} placeholder="Order number (e.g. SEED2601011234)" value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} data-testid="track-order-number-input" />
        <input required className={inp} placeholder="Mobile number used for the order" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="track-mobile-input" />
        <button className="bg-[#1B3B2B] text-white rounded-full px-6 py-3 text-sm" data-testid="track-submit-button">Track</button>
      </form>
      {err && <p className="text-sm text-[#B91C1C] mt-4" data-testid="track-error">{err}</p>}
      {o && (
        <div className="mt-8 border border-[#E8E0D5] rounded-2xl p-5 bg-[#FFFDF9]" data-testid="track-result">
          <p className="font-medium">{o.order_number} · <span className="text-[#2C523A]" data-testid="track-status">{o.status}</span></p>
          {o.tracking?.tracking_number && <p className="text-sm mt-1">Courier: {o.tracking.courier} · {o.tracking.tracking_number} {o.tracking.tracking_url && <a className="underline" href={o.tracking.tracking_url} target="_blank" rel="noreferrer">Track on courier site</a>}</p>}
          <ol className="mt-4 border-l border-[#cfdccf] pl-4 space-y-3">{o.status_history.map((h, i) => <li key={i} className="text-sm"><p className="font-medium">{h.status}</p><p className="text-xs text-stone-500">{fmtDate(h.at, true)}</p></li>)}</ol>
        </div>
      )}
    </div>
  );
}
